/**
 * Centro de Gestión de Clientes — Cuenta Corriente (CRM Fase 1).
 * Reutiliza cuentas-por-cobrar, saldos y pantalla existente por cliente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaFileInvoiceDollar,
  FaSearch,
  FaSync,
  FaUsers
} from 'react-icons/fa';

import { useAuth } from '../../contexts/AuthContext';
import clientesService from '../../services/clientes.service';
import ventasService from '../../services/ventas.service';
import configuracionService from '../../services/configuracion.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import RegistrarPagoDialog from '../../components/modules/ventas/RegistrarPagoDialog';
import CuentaCorrienteKpis from '../../components/modules/cuenta-corriente/CuentaCorrienteKpis';
import CuentaCorrienteTabla from '../../components/modules/cuenta-corriente/CuentaCorrienteTabla';
import {
  buildFilasCuentaCorriente,
  elegirFilaCuentaParaPago,
  extractVentasCliente
} from '../../utils/cuentaCorriente';
import {
  buildFilasCrmCuentaCorriente,
  buildKpisCrmCuentaCorriente,
  CRM_FILTRO_OPCIONES,
  CRM_FILTROS,
  extraerCobrosHoyDesdeResumenPagos,
  filtrarFilasCrmCuentaCorriente
} from '../../utils/crmCuentaCorriente';
import {
  buildResumenDeudaEjecutivo,
  normalizarDeudaVencidaDias,
  parseCuentasPorCobrarResponse
} from '../../utils/dashboardDeuda';

const hoyIso = () => new Date().toISOString().split('T')[0];

const CuentaCorriente = () => {
  const { orgId } = useAuth();

  const [filas, setFilas] = useState([]);
  const [kpis, setKpis] = useState({
    clientesConDeuda: 0,
    totalAdeudado: 0,
    clientesVencidos: 0,
    cobrosRegistradosHoy: 0,
    clientesAlDia: 0
  });
  const [diasCriterio, setDiasCriterio] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroActivo, setFiltroActivo] = useState(CRM_FILTROS.TODOS);
  const [paginaActual, setPaginaActual] = useState(1);
  const [pagoLoadingId, setPagoLoadingId] = useState(null);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [ventaPago, setVentaPago] = useState(null);
  const clientesPorPagina = 25;

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hoy = hoyIso();
      const [config, clientes, cuentasRaw, resumenPagos] = await Promise.all([
        configuracionService.obtener(),
        clientesService.obtenerTodos(orgId),
        ventasService.obtenerCuentasPorCobrar(),
        ventasService.obtenerResumenPagos(hoy, hoy)
      ]);

      const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
      const cuentas = parseCuentasPorCobrarResponse(cuentasRaw);
      const deudaBuilt = buildResumenDeudaEjecutivo(cuentas, diasConfig);
      const filasCrm = buildFilasCrmCuentaCorriente(clientes, cuentasRaw, diasConfig);
      const cobrosHoy = extraerCobrosHoyDesdeResumenPagos(resumenPagos);

      setDiasCriterio(diasConfig);
      setFilas(filasCrm);
      setKpis(buildKpisCrmCuentaCorriente(filasCrm, deudaBuilt.resumen, cobrosHoy));
    } catch (err) {
      console.error('[CuentaCorriente CRM] Error:', err);
      setError(err?.message || 'No se pudo cargar la cuenta corriente');
      setFilas([]);
      setKpis({
        clientesConDeuda: 0,
        totalAdeudado: 0,
        clientesVencidos: 0,
        cobrosRegistradosHoy: 0,
        clientesAlDia: 0
      });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    setPaginaActual(1);
  }, [searchTerm, filtroActivo]);

  const filasFiltradas = useMemo(
    () => filtrarFilasCrmCuentaCorriente(filas, filtroActivo, searchTerm),
    [filas, filtroActivo, searchTerm]
  );

  const totalPaginas = Math.max(1, Math.ceil(filasFiltradas.length / clientesPorPagina));
  const filasPagina = useMemo(() => {
    const inicio = (paginaActual - 1) * clientesPorPagina;
    return filasFiltradas.slice(inicio, inicio + clientesPorPagina);
  }, [filasFiltradas, paginaActual]);

  const handleRegistrarPago = useCallback(async (filaCrm) => {
    setPagoLoadingId(filaCrm.id);
    try {
      const [cuentasRaw, config] = await Promise.all([
        ventasService.obtenerCuentasPorCobrar({ cliente_id: filaCrm.id }),
        configuracionService.obtener()
      ]);
      const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
      let ventas = extractVentasCliente(cuentasRaw, filaCrm.id);
      if (ventas.length === 0 && filaCrm.saldo > 0) {
        const cuentasGlobal = await ventasService.obtenerCuentasPorCobrar();
        ventas = extractVentasCliente(cuentasGlobal, filaCrm.id);
      }
      const filasCc = buildFilasCuentaCorriente(ventas, diasConfig);
      const filaPago = elegirFilaCuentaParaPago(filasCc);
      if (!filaPago) {
        toast.info('Este cliente no tiene ventas adeudadas para registrar pago.');
        return;
      }
      setVentaPago({
        id: filaPago.id,
        numero: filaPago.numero,
        saldo_pendiente: filaPago.saldo,
        total: filaPago.total,
        cliente_nombre: filaCrm.nombre
      });
      setShowPagoDialog(true);
    } catch (err) {
      console.error('[CuentaCorriente CRM] Error al abrir pago:', err);
      toast.error(err?.message || 'No se pudo preparar el registro de pago');
    } finally {
      setPagoLoadingId(null);
    }
  }, []);

  const registrarPago = async (pagoData) => {
    if (!ventaPago?.id) return;
    try {
      await ventasService.registrarPago(ventaPago.id, pagoData);
      toast.success('Pago registrado correctamente');
      setShowPagoDialog(false);
      setVentaPago(null);
      await cargarDatos();
    } catch (err) {
      console.error('Error al registrar pago:', err);
      toast.error(err?.message || 'Error al registrar el pago');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-e0e3-muted mb-1">CRM Comercial</p>
          <h1 className="text-2xl font-bold text-e0e3-foreground flex items-center gap-2">
            <FaFileInvoiceDollar className="text-e0e3-accent" />
            Cuenta Corriente
          </h1>
          <p className="text-sm text-e0e3-muted mt-1">
            Gestión centralizada de saldos, cobros y seguimiento comercial.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button color="secondary" icon={<FaSync />} onClick={cargarDatos} disabled={loading}>
            Actualizar
          </Button>
          <Link to="/clientes">
            <Button color="secondary" icon={<FaUsers />}>
              Módulo Clientes
            </Button>
          </Link>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          {error}
        </div>
      ) : null}

      <CuentaCorrienteKpis kpis={kpis} diasCriterio={diasCriterio} />

      <Card title="Clientes con cuenta corriente" icon={<FaUsers />}>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-xl">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-e0e3-muted" size={14} />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, código, teléfono, CUIT o documento…"
                className="w-full rounded-lg border border-e0e3-border/40 bg-e0e3-surface/40 py-2 pl-9 pr-3 text-sm text-e0e3-foreground placeholder:text-e0e3-muted focus:outline-none focus:ring-2 focus:ring-e0e3-accent/40"
              />
            </div>
            <p className="text-xs text-e0e3-muted shrink-0">
              {filasFiltradas.length} cliente{filasFiltradas.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {CRM_FILTRO_OPCIONES.map((opcion) => (
              <button
                key={opcion.id}
                type="button"
                onClick={() => setFiltroActivo(opcion.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  filtroActivo === opcion.id
                    ? 'border-e0e3-accent/50 bg-e0e3-accent/15 text-e0e3-accent'
                    : 'border-e0e3-border/30 bg-white/[0.03] text-e0e3-muted hover:text-e0e3-foreground'
                }`}
              >
                {opcion.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
              <span className="ml-3 text-e0e3-muted">Cargando clientes…</span>
            </div>
          ) : (
            <>
              <CuentaCorrienteTabla
                filas={filasPagina}
                onRegistrarPago={handleRegistrarPago}
                pagoLoadingId={pagoLoadingId}
              />

              {totalPaginas > 1 ? (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-e0e3-muted">
                    Página {paginaActual} de {totalPaginas}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      disabled={paginaActual <= 1}
                      onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      disabled={paginaActual >= totalPaginas}
                      onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </Card>

      <p className="text-xs text-e0e3-muted">
        Vencimiento configurado: {diasCriterio} días ·{' '}
        <Link to="/configuracion/empresa" className="text-e0e3-accent hover:underline">
          Configurar políticas POS
        </Link>
      </p>

      <RegistrarPagoDialog
        isOpen={showPagoDialog}
        onClose={() => {
          setShowPagoDialog(false);
          setVentaPago(null);
        }}
        venta={ventaPago}
        onPagoRegistrado={registrarPago}
      />
    </div>
  );
};

export default CuentaCorriente;
